/// <amd-module name="Wit/WorkItemForm/Links/Controls/LinksControl" />
import * as React from "react";
import { useCallback, useState, useContext } from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableValue, useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssLayoutManager, usePageContext, useService } from "VSS/Platform/Layout";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Link as AnchorLink } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { LinkableWorkItemTypeFilter, LinkTypeFilter, newLinksFilter } from "Wit/Common/Utils/WorkItemUtils/LinkTypeFilter";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { ILinkCounts, IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { EditCommentDialog } from "Wit/WorkItemForm/Common/Components";
import { EditWorkItemLinkDialog } from "Wit/WorkItemForm/Common/Components/EditWorkItemLinkDialog";
import * as Resources from "Wit/WorkItemForm/Resources";
import { LinkTypeFieldIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkIdentifier, INewLinkData, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { RegisteredWorkItemLinkTypeNames } from "Wit/WorkItemModel/OM";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { Link, WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { useWorkItem } from "../../Common/Hooks";
import { AddLinkDialog } from "../Dialogs/AddLinkDialog";
import { getDevelopmentZeroDataExperienceSettings } from "../GitHub/GitHubLinkTypeService";
import { AddLinkButton } from "./AddLinkButton";
import { CompactLinksList } from "./CompactLinksList";
import { LinksGrid } from "./LinksGrid";
import { LinksZeroState } from "./LinksZeroState";
import { FullScreenSectionContext } from "Wit/WorkItemForm/Common/Components/CollapsibleSection";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

const defaultLinkCountForCompactView = 6;
const defaultLinkCountForCompactExtendedView = 100;

export interface ILinksControlProps {
    compact: boolean;
    linkTypeFilter: LinkTypeFilter;
    zeroDataExperience?: string;
    columns?: string[];
    linkableWorkItemTypeFilter: LinkableWorkItemTypeFilter;
    showZeroDataExperience: boolean;
    controlId?: string;
}

export function LinksControl({
    compact,
    columns,
    linkTypeFilter,
    linkableWorkItemTypeFilter,
    zeroDataExperience,
    showZeroDataExperience,
    controlId
}: ILinksControlProps) {
    const workItem = useWorkItem();
    const pageContext = usePageContext();
    const workItemModelService = useService<IWorkItemModelService>("IWorkItemModelService");
    const layoutManager = useService<IVssLayoutManager>("IVssLayoutManager");
    const linksService = useService<IWorkItemLinksService>("IWorkItemLinksService");
    const workItemService = useService<IWorkItemService>("work-item-service");

    const defaultLinkCount = getInitialLinksCount(compact, controlId);
    const [maxLinkCount, setMaxLinkCount] = useState(defaultLinkCount);

    const linksObservable = linksService.getObservableLinkData(workItem, { linkTypeFilter, maxLinkCount, zeroDataExperience });
    const linkCounts = linksService.getObservableLinkCounts(workItem, { linkTypeFilter, maxLinkCount, zeroDataExperience });

    const [addLinkDialogOpen, setAddLinkDialogOpen] = useObservable(false);
    const [dialogLinkTypeFilter, setDialogLinkTypeFilter] = useObservable<LinkTypeFilter>(() => linkTypeFilter);
    const [linkIdentifierBeingEdited, setLinkIdentifierBeingEdited] = useObservable<ILinkIdentifier | null>(null);
    const [isWorkItemDeleted, setIsWorkItemDeleted] = useObservable<boolean>(workItem.isDeleted());
    const [isWorkItemNew, setIsWorkItemNew] = useObservable<boolean>(workItem.isNew());
    const [workItemId, setWorkItemId] = useState(workItem.id);
    const [showGitHubZeroDataExperience, setShowGitHubZeroDataExperience] = useState(false);
    const [showAzureReposZeroDataExperience, setShowAzureReposZeroDataExperience] = useState(false);

    React.useEffect(() => {
        if (isWorkItemNew.value) {
            const updateAfterSaving = () => {
                const isNew = workItem.isNew();
                if (isWorkItemNew.value !== isNew) {
                    setIsWorkItemNew(isNew);
                }

                const isDeleted = workItem.isDeleted();
                if (isWorkItemDeleted.value !== isDeleted) {
                    setIsWorkItemDeleted(isDeleted);
                }

                // Force rerender if workItem.id is changed to update linksObservable
                if (workItem.id !== workItemId && workItemId === 0) {
                    setWorkItemId(workItem.id);
                }
            };

            workItem.saving.subscribe(updateAfterSaving);
            return () => workItem.saving.unsubscribe(updateAfterSaving);
        }
    }, []);

    React.useEffect(() => {
        getDevelopmentZeroDataExperienceSettings(pageContext).then(data => {
            if (data?.showGitHubZeroDataExperience) {
                setShowGitHubZeroDataExperience(true);
            }
            if (data?.showAzureReposZeroDataExperience) {
                setShowAzureReposZeroDataExperience(true);
            }
        });
    }, []);

    const openAddLinksDialog = useCallback((filter: LinkTypeFilter) => {
        setDialogLinkTypeFilter(() => filter);
        setAddLinkDialogOpen(true);
    }, []);

    const onArtifactsLinked = useCallback(
        (newLinkDisplayData: IResolvedLinkDisplayData[]) => {
            const newLinks = newLinkDisplayData.map(d => Link.createFromLinkData(d.linkIdentifier));
            workItem.addLinks(newLinks, /*keepDeleted*/ true);

            setAddLinkDialogOpen(false);
            // Trigger a reload of the data
            linksService.refreshLinkData(workItem);
        },
        [workItem]
    );

    const onNewLinksUpdated = useCallback(
        async (newLinkData: { newWorkItemData: INewLinkData; linkTypeName: string; comment: string; assignedTo?: string }) => {
            const projectId = getTFSPageData(pageContext)?.project?.id ?? workItem.project.guid;
            if (!projectId) {
                throw new Error(Resources.NoProjectSpecifiedMessage);
            }

            const item = await workItemModelService.createWorkItem(newLinkData.newWorkItemData.typeData, projectId);
            item.addLinks([await WorkItemLink.create(pageContext, newLinkData.linkTypeName, workItem.id, newLinkData.comment)], /*keepDeleted*/ true);
            item.setFieldValueByName(CoreFieldRefNames.Title, newLinkData.newWorkItemData.title);
            item.setFieldValueByName(CoreFieldRefNames.AreaId, workItem.getFieldValueByName(CoreFieldRefNames.AreaId));
            item.setFieldValueByName(CoreFieldRefNames.IterationId, workItem.getFieldValueByName(CoreFieldRefNames.IterationId));
            item.setFieldValueByName(CoreFieldRefNames.AssignedTo, newLinkData.assignedTo);

            layoutManager.renderCallout(onDismiss => (
                <WorkItemFormDialog
                    workItem={item}
                    onDismiss={() => {
                        setAddLinkDialogOpen(false);
                        onDismiss();
                    }}
                    onSaveComplete={async () => {
                        await workItem.refreshNewLinks();
                        linksService.refreshLinkData(workItem);
                    }}
                />
            ));
        },
        [workItem]
    );

    const findLink = useCallback(
        (linkIdentifier: ILinkIdentifier) =>
            workItem
                .getLinks()
                .find(
                    l =>
                        l.linkData.FilePath === linkIdentifier?.FilePath &&
                        l.linkData.ID === linkIdentifier?.ID &&
                        l.linkData.LinkType === linkIdentifier?.LinkType
                ),
        [workItem]
    );

    const onCommitComment = useCallback(
        (newComment: string) => {
            if (!linkIdentifierBeingEdited.value) {
                return;
            }
            const link = findLink(linkIdentifierBeingEdited.value);
            if (!link) {
                return;
            }
            // Update the comment on the display data so that the UI updates immediately
            linkIdentifierBeingEdited.value.Comment = newComment;
            // Update the comment on the link model so that the work item state is correct
            link.setComment(newComment);
            workItem.evaluateDirty();
            // Close the dialog
            setLinkIdentifierBeingEdited(null);
            // Trigger a reload of the data
            linksService.refreshLinkData(workItem);
        },
        [workItem]
    );

    const onDeleteLink = useCallback(
        (linkIdentifier: ILinkIdentifier) => {
            const link = findLink(linkIdentifier);
            if (!link) {
                return;
            }
            workItem.removeLinks([link], /*keepDeleted*/ true);
            linksService.refreshLinkData(workItem);
        },
        [workItem]
    );

    const dialogs = (
        <Observer newLinkTypeFilter={dialogLinkTypeFilter}>
            {(observableProps: { newLinkTypeFilter: LinkTypeFilter }) => {
                return (
                    <>
                        <ConditionalChildren renderChildren={addLinkDialogOpen}>
                            <AddLinkDialog
                                originWorkItems={[workItem]}
                                linkTypeFilter={observableProps.newLinkTypeFilter}
                                linkableWorkItemTypeFilter={linkableWorkItemTypeFilter}
                                onAddLinks={onArtifactsLinked}
                                onNewLinksAdded={onNewLinksUpdated}
                                onDismiss={() => setAddLinkDialogOpen(false)}
                                isLinkToNewItem={observableProps.newLinkTypeFilter === newLinksFilter}
                            />
                        </ConditionalChildren>
                        <Observer linkIdentifier={linkIdentifierBeingEdited}>
                            {({ linkIdentifier }: { linkIdentifier: ILinkIdentifier | null }) => {
                                if (!linkIdentifier) {
                                    return null;
                                }
                                return linkIdentifier.FldID !== LinkTypeFieldIds.WorkItem ? (
                                    <EditCommentDialog
                                        comment={linkIdentifier.Comment ?? ""}
                                        onCommitComment={onCommitComment}
                                        onDismiss={() => setLinkIdentifierBeingEdited(null)}
                                    />
                                ) : (
                                    <EditWorkItemLinkDialog
                                        comment={linkIdentifier.Comment ?? ""}
                                        onEditLink={({ comment, linksToAdd, removeLink }) => {
                                            if (!removeLink) {
                                                onCommitComment(comment);
                                            }
                                            if (removeLink) {
                                                onDeleteLink(linkIdentifier);
                                            }
                                            if (linksToAdd && linksToAdd.length > 0) {
                                                onArtifactsLinked(linksToAdd);
                                            }
                                            setLinkIdentifierBeingEdited(null);
                                        }}
                                        onDismiss={() => setLinkIdentifierBeingEdited(null)}
                                        linkIdentifier={linkIdentifier}
                                        workItem={workItem}
                                    />
                                );
                            }}
                        </Observer>
                    </>
                );
            }}
        </Observer>
    );

    const renderAddLinkButton = (workItemIsDeleted: boolean, isWorkItemNew: boolean): JSX.Element | null => {
        if (workItemIsDeleted) {
            return null;
        } else {
            return (
                <AddLinkButton
                    onLinkToExistingItem={() => openAddLinksDialog(linkTypeFilter)}
                    onLinkToNewItem={() => openAddLinksDialog(newLinksFilter)}
                    linkTypeFilter={linkTypeFilter}
                    isNewItemLinkDisabled={isWorkItemNew}
                />
            );
        }
    };

    function onDismissWorkItemForm(id?: number): void {
        id && workItemService.deleteItemsFromCache([id]);
        linksService.refreshLinkData(workItem, { linkTypeFilter, maxLinkCount, ignoreArtifactCache: true });
    }

    return (
        <Observer links={linksObservable} isWorkItemNew={isWorkItemNew} isWorkItemDeleted={isWorkItemDeleted}>
            {({
                links,
                isWorkItemNew,
                isWorkItemDeleted
            }: {
                links: (IResolvedLinkDisplayData | undefined)[];
                isWorkItemNew: boolean;
                isWorkItemDeleted: boolean;
            }) => {
                const content = [];
                const isNewZeroStateBehaviorDisabled = isFeatureFlagEnabled(
                    pageContext,
                    FeatureFlags.DisableNewWorkItemZeroStateCleanUpBehavior,
                    false
                );

                if (links.length === 0) {
                    if (zeroDataExperience === "Development" && showGitHubZeroDataExperience) {
                        content.push(
                            <LinksZeroState
                                key="zeroGitHubLinks"
                                zeroDataExperience={zeroDataExperience}
                                openAddLinksDialog={openAddLinksDialog}
                                linkTypeFilter={linkTypeFilter}
                                type="zeroGitHubLinks"
                            />
                        );
                    }
                    if (isNewZeroStateBehaviorDisabled) {
                        content.push(
                            <LinksZeroState
                                key="zeroLinks"
                                zeroDataExperience={zeroDataExperience}
                                openAddLinksDialog={openAddLinksDialog}
                                linkTypeFilter={linkTypeFilter}
                            />
                        );
                    } else {
                        if (zeroDataExperience !== "Development" || showAzureReposZeroDataExperience) {
                            content.push(
                                <LinksZeroState
                                    key="zeroLinks"
                                    zeroDataExperience={zeroDataExperience}
                                    openAddLinksDialog={openAddLinksDialog}
                                    linkTypeFilter={linkTypeFilter}
                                />
                            );
                        }
                    }
                } else {
                    // if work item does not have parent but has child/children - we still want to render 'Add an existing work item as a parent' control
                    if (
                        showZeroDataExperience &&
                        zeroDataExperience === "RelatedWork" &&
                        !links.some(l => l?.linkTypeDisplayName === RegisteredWorkItemLinkTypeNames.Parent)
                    ) {
                        content.push(
                            <LinksZeroState
                                key="noParent"
                                zeroDataExperience={zeroDataExperience}
                                openAddLinksDialog={openAddLinksDialog}
                                linkTypeFilter={linkTypeFilter}
                            />
                        );
                    }

                    if (compact) {
                        content.push(
                            <React.Fragment key="compact">
                                <CompactLinksList links={links} onDeleteLink={onDeleteLink} onDismissWorkItemForm={onDismissWorkItemForm} />
                                <ShowMoreLink counts={linkCounts} onShowMore={() => setMaxLinkCount(maxLinkCount * 2)} />
                            </React.Fragment>
                        );
                    } else {
                        content.push(
                            <LinksGrid
                                key="notCompact"
                                links={links}
                                onEditLink={linkIdentifier => setLinkIdentifierBeingEdited(linkIdentifier)}
                                onDeleteLink={onDeleteLink}
                                columns={columns}
                            />
                        );
                    }
                }

                return (
                    <>
                        {renderAddLinkButton(isWorkItemDeleted, isWorkItemNew)}
                        {content}
                        {dialogs}
                    </>
                );
            }}
        </Observer>
    );
}

function getInitialLinksCount(compact: boolean, controlId?: string): number {
    const children = useContext(FullScreenSectionContext).fullScreenContent?.props.children;
    const isInFullScreen = controlId && Array.isArray(children) && children.some(ch => ch.key?.toString().endsWith(controlId));
    if (compact) {
        return isInFullScreen ? defaultLinkCountForCompactExtendedView : defaultLinkCountForCompactView;
    } else {
        return Number.MAX_SAFE_INTEGER;
    }
}

function ShowMoreLink({ counts, onShowMore }: { counts: IObservableValue<ILinkCounts>; onShowMore: () => void }): JSX.Element {
    return (
        <Observer counts={counts}>
            {({ counts }: { counts: ILinkCounts }) => {
                if (counts.shown === counts.total) {
                    return null;
                }

                return (
                    <div className="flex-column">
                        <div className="flex-row">
                            <AnchorLink onClick={onShowMore}>{Resources.LinksShowMore}</AnchorLink>
                            <div className="margin-left-4">{format(Resources.LinksShownOfTotal, counts.shown, counts.total)}</div>
                        </div>
                        <div>
                            {format(
                                Resources.LinksNotShown,
                                Object.entries(counts.notShown)
                                    .map(([linkType, count]) => `${linkType} (${count})`)
                                    .join(", ")
                            )}
                        </div>
                    </div>
                );
            }}
        </Observer>
    );
}
